import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType, NotFoundException } from '@zxing/library';
import { Loader2, AlertTriangle, RefreshCw, ScanLine } from 'lucide-react';
import { Button } from './ui/button';

/**
 * iter 100 — In-browser PDF417 scanner for US/CA state IDs.
 *
 * Real-world driver's-license PDF417 detection is HARD. The JS port
 * of ZXing is known to be ~40% accurate on real IDs (vs. ~99% for
 * native iOS / Android decoders). We use a 3-tier strategy so the
 * user lands on the most reliable decoder their device supports:
 *
 *   1. **Native BarcodeDetector API** (iOS Safari 17+, Chrome 83+,
 *      Edge). Native PDF417 support, runs at full GPU-accelerated
 *      framerate. This is our primary path — should hit close to
 *      100% on a clean ID under decent lighting.
 *
 *   2. **ZXing fallback** for browsers without BarcodeDetector or
 *      without PDF417 support in their detector. Configured with
 *      tight scan-attempt loop (50ms) + TRY_HARDER + 1080p video
 *      so it has the best shot at locking on.
 *
 *   3. **Tap-to-capture** — a large button that grabs the current
 *      video frame and runs BOTH decoders against it. The user
 *      controls timing, which beats passive auto-detect on poor
 *      lighting / shaky hand.
 *
 *   4. **Upload-image fallback** — for users where the camera is
 *      blocked entirely (corporate phones, locked iOS profiles).
 *
 * Camera frames NEVER leave the device. Only the decoded AAMVA
 * payload is POSTed to the server.
 */
const IdBarcodeScanner = ({ onScan, onCancel }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const zxingControlsRef = useRef(null);
  const detectorRef = useRef(null);
  const rafRef = useRef(null);
  const fileInputRef = useRef(null);
  const firedRef = useRef(false);
  const onScanRef = useRef(onScan);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(true);
  const [decoder, setDecoder] = useState('starting');     // 'native' | 'zxing' | 'starting'
  const [attempts, setAttempts] = useState(0);             // visible counter, throttled to 1Hz
  const attemptsRef = useRef(0);                            // hot counter, never re-renders
  const [tapping, setTapping] = useState(false);
  const [tapResult, setTapResult] = useState(null);        // { ok: bool, msg: str }

  const fire = useCallback((rawText) => {
    if (firedRef.current) return;
    firedRef.current = true;
    // Stop everything
    try { zxingControlsRef.current?.stop(); } catch { /* noop */ }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    }
    onScanRef.current?.(rawText);
  }, []);

  // iter 100 — Throttle the visible "frames analyzed" counter to 1Hz.
  // Bumping React state inside the rAF loop (60fps) was the perf
  // killer — it triggered a full re-render of the scanner + parent
  // every animation frame, which on a phone absolutely tanks the
  // whole tab while the camera is also streaming. We bump a ref
  // every frame (free) and only push the value to React state once
  // a second.
  useEffect(() => {
    const t = setInterval(() => {
      setAttempts(attemptsRef.current);
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // Start the camera + the appropriate decoder.
  useEffect(() => {
    let mounted = true;

    const start = async () => {
      try {
        // 1. Get the camera stream ourselves so we can run BOTH
        //    BarcodeDetector AND a manual-tap canvas grab against
        //    the same video element.
        const constraints = {
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (!videoRef.current) return;  // unmount race
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch { /* user-gesture race */ }

        // Wait for the video to actually have decodable frames before
        // we start the decoder loop. BarcodeDetector throws on a
        // video element with readyState < 2.
        if (videoRef.current.readyState < 2) {
          await new Promise((resolve) => {
            const v = videoRef.current;
            if (!v) { resolve(); return; }
            const onReady = () => { v.removeEventListener('loadeddata', onReady); resolve(); };
            v.addEventListener('loadeddata', onReady, { once: true });
            // Failsafe — never block forever
            setTimeout(resolve, 3000);
          });
        }
        if (!mounted || !videoRef.current) return;

        // 2. Pick a decoder. BarcodeDetector → native; else ZXing.
        let useNative = false;
        if (typeof window !== 'undefined' && 'BarcodeDetector' in window) {
          try {
            const supported = (typeof window.BarcodeDetector.getSupportedFormats === 'function')
              ? await window.BarcodeDetector.getSupportedFormats()
              : ['pdf417'];
            if (Array.isArray(supported) && supported.includes('pdf417')) {
              detectorRef.current = new window.BarcodeDetector({ formats: ['pdf417'] });
              useNative = true;
            }
          } catch { /* fall through to ZXing */ }
        }

        if (useNative) {
          setDecoder('native');
          if (mounted) setStarting(false);
          // rAF loop — analyze every animation frame
          const loop = async () => {
            if (firedRef.current || !mounted) return;
            const v = videoRef.current;
            if (!v || v.readyState < 2) {
              rafRef.current = requestAnimationFrame(loop);
              return;
            }
            try {
              const codes = await detectorRef.current.detect(v);
              attemptsRef.current += 1;
              if (codes && codes.length > 0 && codes[0].rawValue) {
                fire(codes[0].rawValue);
                return;
              }
            } catch { /* swallow per-frame errors */ }
            rafRef.current = requestAnimationFrame(loop);
          };
          rafRef.current = requestAnimationFrame(loop);
        } else {
          setDecoder('zxing');
          if (mounted) setStarting(false);
          // ZXing — tight scan loop, PDF417 only, TRY_HARDER on.
          const hints = new Map();
          hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.PDF_417]);
          hints.set(DecodeHintType.TRY_HARDER, true);
          const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 50 });
          const controls = await reader.decodeFromVideoElement(
            videoRef.current,
            (result, err) => {
              if (!mounted) return;
              attemptsRef.current += 1;
              if (result) {
                fire(result.getText());
                return;
              }
              if (err && !(err instanceof NotFoundException)) {
                // ignore checksum/etc — next frame retries
              }
            }
          );
          zxingControlsRef.current = controls;
        }
      } catch (e) {
        if (!mounted) return;
        setStarting(false);
        if (e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError') {
          setError('Camera permission was denied. Allow camera access in your browser settings, or upload a photo of your ID below.');
        } else if (e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError') {
          setError('No back camera detected. Upload a photo of your ID below.');
        } else if (e?.name === 'NotReadableError') {
          setError('Camera is in use by another app. Close other camera apps and retry, or upload a photo of your ID below.');
        } else {
          setError('Could not start the camera (' + (e?.name || 'unknown') + '). Upload a photo of your ID below.');
        }
      }
    };

    start();

    return () => {
      mounted = false;
      try { zxingControlsRef.current?.stop(); } catch { /* noop */ }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) {
        try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tap-to-capture: grab the current frame from the <video>, draw
  // it to an offscreen canvas, and run BOTH decoders against it.
  // Beats passive auto-scan when the auto-loop is missing the lock.
  const handleTapCapture = async () => {
    if (tapping || !videoRef.current) return;
    setTapping(true);
    setTapResult(null);
    try {
      const v = videoRef.current;
      const w = v.videoWidth || 1280;
      const h = v.videoHeight || 720;
      if (!canvasRef.current) canvasRef.current = document.createElement('canvas');
      canvasRef.current.width = w;
      canvasRef.current.height = h;
      const ctx = canvasRef.current.getContext('2d');
      ctx.drawImage(v, 0, 0, w, h);

      // Try native first if available
      if (detectorRef.current) {
        try {
          const codes = await detectorRef.current.detect(canvasRef.current);
          if (codes && codes.length > 0 && codes[0].rawValue) {
            fire(codes[0].rawValue);
            return;
          }
        } catch { /* try ZXing */ }
      }

      // ZXing on the still
      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.PDF_417]);
      hints.set(DecodeHintType.TRY_HARDER, true);
      const reader = new BrowserMultiFormatReader(hints);
      const dataUrl = canvasRef.current.toDataURL('image/png');
      try {
        const result = await reader.decodeFromImageUrl(dataUrl);
        if (result) {
          fire(result.getText());
          return;
        }
      } catch (_e) { /* fall through */ }

      setTapResult({ ok: false, msg: "Couldn't read the barcode. Move closer, hold steady, and try again." });
    } catch (e) {
      setTapResult({ ok: false, msg: 'Capture failed: ' + (e?.message || 'unknown') });
    } finally {
      setTapping(false);
    }
  };

  // Upload fallback
  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTapResult({ ok: null, msg: 'Decoding image…' });
    try {
      const url = URL.createObjectURL(file);
      try {
        // Native first
        if (detectorRef.current) {
          const img = new Image();
          img.src = url;
          await new Promise((res) => { img.onload = res; img.onerror = res; });
          try {
            const codes = await detectorRef.current.detect(img);
            if (codes && codes.length > 0 && codes[0].rawValue) {
              fire(codes[0].rawValue);
              return;
            }
          } catch { /* try ZXing */ }
        }
        // ZXing
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.PDF_417]);
        hints.set(DecodeHintType.TRY_HARDER, true);
        const reader = new BrowserMultiFormatReader(hints);
        const result = await reader.decodeFromImageUrl(url);
        if (result) { fire(result.getText()); return; }
      } finally {
        URL.revokeObjectURL(url);
      }
      setTapResult({ ok: false, msg: "Couldn't read the barcode in that image. Try a sharper, closer shot." });
    } catch (err) {
      setTapResult({ ok: false, msg: "Couldn't read the barcode in that image. " + (err?.message || '') });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <div
        className="relative w-full aspect-[4/3] bg-black rounded-xl overflow-hidden shadow-lg"
        data-testid="id-scanner-frame"
      >
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="w-full h-full object-cover"
        />
        {/* ID outline overlay */}
        {!error && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div
              className="border-2 border-white/80 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.45)] relative"
              style={{ width: '85%', aspectRatio: '85/54' }}
            >
              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-emerald-300 rounded-tl-lg -translate-x-1 -translate-y-1" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-emerald-300 rounded-tr-lg translate-x-1 -translate-y-1" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-emerald-300 rounded-bl-lg -translate-x-1 translate-y-1" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-emerald-300 rounded-br-lg translate-x-1 translate-y-1" />
            </div>
          </div>
        )}

        {/* Status pill — shows which decoder is active + how many frames analyzed */}
        {!error && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-black/65 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5" data-testid="id-scanner-status">
            {starting ? (
              <><Loader2 className="w-3 h-3 animate-spin" /> Starting camera…</>
            ) : (
              <>
                <ScanLine className="w-3 h-3 animate-pulse" />
                {decoder === 'native' ? 'Auto-scanning' : 'Auto-scanning (legacy)'}
                <span className="opacity-60 ml-1">· {attempts}</span>
              </>
            )}
          </div>
        )}

        {/* Cancel pill */}
        {!error && (
          <button
            type="button"
            onClick={onCancel}
            className="absolute top-3 right-3 bg-white/90 text-black text-xs font-medium px-3 py-1.5 rounded-full"
            data-testid="id-scanner-cancel-btn"
          >
            Cancel
          </button>
        )}

        {/* Error overlay */}
        {error && (
          <div className="absolute inset-0 bg-black/85 flex items-center justify-center p-5" data-testid="id-scanner-error">
            <div className="text-center max-w-xs">
              <div className="w-12 h-12 mx-auto mb-3 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-300" />
              </div>
              <p className="text-sm text-white font-medium mb-1">Camera unavailable</p>
              <p className="text-xs text-white/80">{error}</p>
            </div>
          </div>
        )}
      </div>

      {/* Tap-to-capture — primary user action when auto isn't locking on */}
      {!error && (
        <Button
          type="button"
          className="w-full h-12 bg-[#586D52] hover:bg-[#465A41] text-white"
          onClick={handleTapCapture}
          disabled={starting || tapping}
          data-testid="id-scanner-tap-capture-btn"
        >
          {tapping ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Decoding…</>) : (<><ScanLine className="w-4 h-4 mr-2" />Tap to capture & scan</>)}
        </Button>
      )}

      {/* Tap result hint */}
      {tapResult && (
        <p className={`text-xs ${tapResult.ok === false ? 'text-red-700 dark:text-red-300' : 'text-muted-foreground'}`} data-testid="id-scanner-tap-result">
          {tapResult.msg}
        </p>
      )}

      {/* Retry control — shown only on camera error. The upload-photo
          fallback was intentionally removed; barcode scanning happens
          on-camera only. */}
      {error && (
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileChosen}
            className="hidden"
            data-testid="id-scanner-file-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => window.location.reload()}
            data-testid="id-scanner-retry-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" />
            Retry
          </Button>
        </div>
      )}
      {error && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={onCancel}
          data-testid="id-scanner-back-btn"
        >
          Back
        </Button>
      )}
    </div>
  );
};

export default IdBarcodeScanner;
